import {
  CustomSelectChoice,
  FieldAnswer,
  FieldType,
  MultipleChoiceField,
  Session,
  SingleChoiceField,
} from '@app/api';
import React, { useMemo } from 'react';
import { MultipleChoiceFieldForm } from './CustomForm/FormField/MultipleChoicesFieldForm';
import { SingleChoiceFieldForm } from './CustomForm/FormField/SingleChoiceFieldForm';
import { SubmitType } from './CustomForm/fieldConfigurations';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { DEVICE_CATEGORY_ICONS } from '../ParticipationSummary/components/TechnicalRequirements';

const DEVICE_CHOICE_REF = 'ALLOWED_DEVICES_CATEGORY';

interface Props {
  session: Session;
  onDeviceChoiceSubmit: (answer: FieldAnswer) => void;
}

export const ParticipantDeviceChoice = ({
  session,
  onDeviceChoiceSubmit,
}: Props) => {
  const { t } = useTranslation();

  const allowedDevices = useMemo(
    () => session?.allowedDevicesCategories || [],
    [session],
  );

  const field = useMemo(() => {
    if (allowedDevices.length > 1) {
      const choices = [
        ...allowedDevices.map((device) => ({
          ref: device,
          label: t(`devicesSelection.devices.${device.toLowerCase()}`),
          icon: DEVICE_CATEGORY_ICONS[device],
        })),
        {
          ref: CustomSelectChoice.None,
          label: t('screeningForm.none'),
        },
      ];

      const multipleChoiceField: MultipleChoiceField = {
        id: uuidv4(),
        ref: DEVICE_CHOICE_REF,
        title: t('devicesSelection.screening.multipleChoicesTitle'),
        subTitle: t('devicesSelection.screening.choiceWarning'),
        type: FieldType.MULTIPLE_CHOICE,
        properties: {
          randomize: false,
          choices,
        },
        validation: {
          required: true,
        },
      };

      return multipleChoiceField;
    } else {
      const device = allowedDevices[0];
      const deviceName =
        device &&
        t(`devicesSelection.devices.${device.toLowerCase()}`).toLowerCase();
      const choices = [
        {
          ref: device,
          label: t('devicesSelection.screening.singleChoiceHave'),
        },
        {
          ref: CustomSelectChoice.None,
          label: t('devicesSelection.screening.singleChoiceDontHave'),
        },
      ];

      const singleChoiceField: SingleChoiceField = {
        id: uuidv4(),
        ref: DEVICE_CHOICE_REF,
        title: t('devicesSelection.screening.singleChoiceTitle', {
          deviceNeeded: deviceName,
        }),
        type: FieldType.SINGLE_CHOICE,
        properties: {
          randomize: false,
          choices,
        },
        validation: {
          required: true,
        },
      };
      return singleChoiceField;
    }
  }, [allowedDevices, t]);

  return allowedDevices.length > 1 ? (
    <MultipleChoiceFieldForm
      field={field as MultipleChoiceField}
      onSubmit={onDeviceChoiceSubmit}
      submitType={SubmitType.CONTINUE}
    />
  ) : (
    <SingleChoiceFieldForm
      field={field as SingleChoiceField}
      onSubmit={onDeviceChoiceSubmit}
      submitType={SubmitType.CONTINUE}
    />
  );
};
